import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import '../styles/DraggableSection.css';
import { FaExpandAlt, FaCompressAlt, FaWindowMaximize, FaWindowRestore, FaTimes } from 'react-icons/fa';
import TaskList from './TaskList';

const DraggableSection = ({ 
    section,
    darkMode, 
    opacity = 0.85,
    blur = 1.0,
    baseColor = null,
    isVisible = true,
    index,
    children,
    onClose,
    onVisibilityChange,
    isMenuCollapsed = false,
    panels = [],
    sectionVisibility = {},
    onSectionVisibilityChange
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    if (!section || !isVisible) return null;

    // Convert hex to RGB for background tinting
    const hexToRgb = (hex) => {
        if (!hex) return null;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };
    
    const getBackgroundColor = () => {
        if (!baseColor) {
            // Default fallback if no base color
            return darkMode ? `rgba(45, 45, 45, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
        }
        
        // Convert base color to RGB
        const rgb = hexToRgb(baseColor);
        if (!rgb) return darkMode ? `rgba(45, 45, 45, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
        
        if (darkMode) {
            // Dark mode: Use the actual base color with opacity
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
        } else {
            // Light mode: Use a lighter tint of the base color
            // Mix with white to create a pleasant light version
            const lightFactor = 0.35; // Keep 35% of the original color
            const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
            const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
            const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
            return `rgba(${lightR}, ${lightG}, ${lightB}, ${opacity})`;
        }
    };

    const handleClose = (e) => {
        e.stopPropagation();
        if (onClose) {
            onClose(section.id);
        }
    };

    const handleMinimize = (e) => {
        e.stopPropagation();
        setIsMinimized(!isMinimized);
        if (isMaximized) {
            setIsMaximized(false);
        }
        if (onVisibilityChange) {
            onVisibilityChange(section.id, 'minimize');
        }
    };

    const handleMaximize = (e) => {
        e.stopPropagation();
        setIsMaximized(!isMaximized);
        if (isMinimized) {
            setIsMinimized(false);
        }
        if (onVisibilityChange) {
            onVisibilityChange(section.id, 'maximize');
        }
    };

    return (
        <Draggable draggableId={section.id.toString()} index={index}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`draggable-section 
                        ${isMinimized ? 'minimized' : ''} 
                        ${isMaximized ? 'maximized' : ''} 
                        ${darkMode ? 'dark-mode' : ''} 
                        ${section.code === 'Main' ? 'main-section' : ''}
                        ${isMenuCollapsed ? 'menu-collapsed' : ''}`}
                    style={{
                        ...provided.draggableProps.style,
                        '--local-opacity': opacity,
                        '--local-blur': blur,
                        backgroundColor: getBackgroundColor()
                    }}
                >
                    <div 
                        className="section-header"
                        {...provided.dragHandleProps}
                    >
                        <div className="section-title">
                            <span>{section.name}</span>
                            {section.description && (
                                <p className="section-description">{section.description}</p>
                            )}
                        </div>
                        <div className="section-controls">
                            <button
                                className="control-button minimize-button"
                                onClick={handleMinimize}
                                title={isMinimized ? "Expand" : "Minimize"}
                            >
                                {isMinimized ? <FaExpandAlt /> : <FaCompressAlt />}
                            </button>
                            <button
                                className="control-button maximize-button"
                                onClick={handleMaximize}
                                title={isMaximized ? "Restore" : "Maximize"}
                            >
                                {isMaximized ? <FaWindowRestore /> : <FaWindowMaximize />}
                            </button>
                            <button
                                className="control-button close-button"
                                onClick={handleClose}
                                title="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    </div>
                    {!isMinimized && (
                        <div className={`section-content ${isMaximized ? 'maximized' : ''}`}>
                            {section.code === 'Main' || section.code === 'Modules' ? children : 
                             section.code === 'Tasks' ? (
                                <TaskList 
                                    panels={panels}
                                    sectionVisibility={sectionVisibility}
                                    onSectionVisibilityChange={onSectionVisibilityChange}
                                    darkMode={darkMode}
                                />
                             ) : (
                                <div className="placeholder-content">
                                    Content for {section.name}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Draggable>
    );
};

export default DraggableSection; 